import { PlusCircleOutlined, GlobalOutlined } from "@ant-design/icons";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { IconButton, Box } from "@mui/material";
import { Popconfirm, message } from "antd";
import { useEffect, useState } from "react";
import axiosInstance from "../../../axios/axios-instance";
import AddCountryModal from "../../../components/AddCountry";
import ActionButton from "../../../components/common/buttons/ActionButton";
import Card from "../../../components/common/Card";
import { STATUS_PILL_CLASSES } from "../../../theme/statusColors";
import MuiTable from "../../../components/common/MuiTable";
import { useMe } from "../../../auth/MeContext";
import { PERM } from "../../../auth/permissions";
import { useCountries } from "../../../hooks/useCountries";
import { Column } from "../../../utils/constants";
import { pathAPI } from "../../../utils/pathApi";
import LocalModal from "./LocalModal";
import TranslationModal from "./TranslationModal";

interface Province {
  id: number;
  name: string;
  active?: boolean;
}

interface CountryData {
  id: number;
  name: string;
  code: string;
  flag: string;
  currency: string;
  phone_code: string;
  language_id: number;
  active: boolean;
  action?: number;
  i18n_names?: {
    en: string;
    [key: string]: string;
  };
  country_short_code?: string;
  provinces?: Province[];
  get country_code(): string;
}

const Country = () => {
  const [openModal, setOpenModal] = useState(false);
  const [{ data: countries, isLoading: loadingCountries }, { mutate }] = useCountries();
  const { can } = useMe();
  const canCreate = can(PERM.COUNTRY_CREATE);
  const canUpdate = can(PERM.COUNTRY_UPDATE);
  const canDelete = can(PERM.COUNTRY_DELETE);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [data, setData] = useState<CountryData[]>([]);
  const [locationModalVisible, setLocationModalVisible] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<CountryData | undefined>(undefined);
  const [editingCountry, setEditingCountry] = useState<CountryData | undefined>(undefined);
  const [translationModalVisible, setTranslationModalVisible] = useState(false);
  var currentLang: any = localStorage.getItem("language");

  const [messageApi, contextHolder] = message.useMessage();

  const handleClickEdit = async (id: number) => {
    try {
      const response = await axiosInstance.get(`${pathAPI.countries}/${id}`);
      if (response.data?.data) {
        setEditingCountry(response.data.data);
        setOpenModal(true);
      }
    } catch (error) {
      console.error("Error fetching country details:", error);
      messageApi.open({
        type: "error",
        content: "Failed to fetch country details"
      });
    }
  };

  const deleteSuccess = () => {
    messageApi.open({
      type: "success",
      content: "Country deleted successfully!",
    });
  };

  const deleteError = () => {
    messageApi.open({
      type: "error",
      content: "Something went wrong!",
    });
  };

  const handleDeleteCountry = async (id: number) => {
    try {
      setLoadingDelete(true);
      await axiosInstance.delete(`${pathAPI.countries}/${id}`);
      deleteSuccess();
      // Reload data after deletion
      mutate();
    } catch (error) {
      deleteError();
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleManageLocations = (country: CountryData) => {
    setSelectedCountry(country);
    setLocationModalVisible(true);
  };

  const handleManageTranslations = (country: CountryData) => {
    setSelectedCountry(country);
    setTranslationModalVisible(true);
  };

  const header: Column[] = [
    {
      id: "id",
      label: "ID",
      minWidth: 10,
      align: "center",
    },
    {
      id: "flag",
      label: "Flag",
      minWidth: 100,
      align: "center",
      format: (value: string) => {
        return (
          <img
            src={value}
            alt="country flag"
            className="w-12 h-12 rounded-lg m-auto"
          />
        );
      },
    },
    {
      id: "name",
      label: "Name",
      minWidth: 150,
      align: "center",
      format: (value: string) => {
        return <div className="font-bold">{value}</div>;
      },
    },
    {
      id: "phone_code",
      label: "Phone Code",
      minWidth: 100,
      align: "center",
      format: (value: string) => {
        return <div className="font-bold">{value}</div>;
      },
    },
    {
      id: "currency",
      label: "Currency",
      minWidth: 100,
      align: "center",
      format: (value: string) => {
        return <div className="font-bold">{value}</div>;
      },
    },
    {
      id: "active",
      label: "Status",
      minWidth: 20,
      align: "center",
      format: (value: boolean) => {
        return (
          <div
            className={`px-2 rounded-lg font-bold ${
              value ? STATUS_PILL_CLASSES.SUCCESS : STATUS_PILL_CLASSES.DRAFT
            }`}
          >
            {value ? "Active" : "Inactive"}
          </div>
        );
      },
    },
    {
      id: "provinces",
      label: "Provinces",
      minWidth: 150,
      align: "center",
      format: (value: any) => {
        if (!value) return null;
        // Value is an object with countryId and provinces data
        const { countryId, provinces } = value;
        const row = data.find((item) => item.action === countryId);
        if (!row) return null;

        return (
          <div
            onClick={() => handleManageLocations(row)}
            className="cursor-pointer hover:opacity-75 transition-opacity"
            title="Click to manage locations"
          >
            <div className="text-blue-400 font-bold text-lg flex items-center justify-center">
              {provinces}
            </div>
          </div>
        );
      },
    },
    {
      id: "action",
      label: "Actions",
      minWidth: 120,
      align: "center",
      format: (value: any) => {
        const row = data.find((item) => item.action === value);
        if (!row) return null;

        return (
          <Box
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            gap={1}
          >
            <Box
              size={"small"}
              component={IconButton}
              disabled={loadingDelete || !canUpdate}
              onClick={() => handleClickEdit(value)}
              title={canUpdate ? "Edit" : "No permission to edit"}
            >
              <EditIcon color={canUpdate ? "success" : "disabled"} />
            </Box>
            <Box
              size={"small"}
              component={IconButton}
              disabled={!canUpdate}
              onClick={() => handleManageTranslations(row)}
              title={canUpdate ? "Translations" : "No permission to edit translations"}
            >
              <GlobalOutlined style={{ fontSize: '20px', color: canUpdate ? '#1890ff' : '#bfbfbf' }} />
            </Box>
            <Popconfirm
              title="Are you sure you want to delete this country?"
              okText="Yes"
              cancelText="No"
              disabled={!canDelete}
              onConfirm={() => handleDeleteCountry(value)}
              okButtonProps={{
                style: { background: "#ba2156", border: "none" },
                loading: loadingDelete,
              }}
            >
              <Box
                size={"small"}
                component={IconButton}
                disabled={!canDelete}
                title={canDelete ? "Delete" : "No permission to delete"}
              >
                <DeleteIcon color={canDelete ? "error" : "disabled"} />
              </Box>
            </Popconfirm>
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    if (countries) {
      const updatedData = countries.map((item: any) => ({
        ...item,
        action: item.id,
        provinces: {
          countryId: item.id,
          provinces: item.provinces || []
        }
      }));
      setData(updatedData);
    }
  }, [countries]);

  const openModalAddNew = () => {
    setOpenModal(true);
    setEditingCountry(undefined);
  };

  return (
    <Card className="user">
      <div className="filter-button mb-3 flex justify-end">
        <ActionButton
          appSize="large"
          startIcon={<PlusCircleOutlined />}
          disabled={!canCreate}
          onClick={openModalAddNew}
        >
          Add New
        </ActionButton>
      </div>
      <MuiTable header={header} data={data} isLoading={loadingCountries}></MuiTable>
      {contextHolder}
      <AddCountryModal
        open={openModal}
        reloadData={() => mutate()}
        onCancel={() => {
          setOpenModal(false);
          setEditingCountry(undefined);
        }}
        countryData={editingCountry}
      />
      <LocalModal
        open={locationModalVisible}
        onClose={() => setLocationModalVisible(false)}
        countryId={selectedCountry?.id}
        countryName={selectedCountry?.name}
        languageCode={currentLang}
      />
      {translationModalVisible && (
        <TranslationModal
          open={translationModalVisible}
          onClose={() => setTranslationModalVisible(false)}
          country={selectedCountry}
        />
      )}
    </Card>
  );
};
export default Country;
